import {
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import Button from "@mui/material/Button";
import React, {useState} from "react";
import {CustomTheme} from "../../../../config/theme/theme";
import {useTranslation} from "react-i18next";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers";
import useData from "../../../../hooks/useData";
import {startOfDay} from "date-fns";
import {GetDataExportApiSitesSiteIdMeteringExportGetColumnsEnum} from "../../../../services";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {nl} from "date-fns/locale";
import moment from "moment";

const columnTranslations: Map<string, string> = new Map([
    ["utctime", "UTC"],
    ["consumption", "Verbruik (kW)"],
    ["production", "Productie (kW)"],
    ["flex_charge", "Batterij opladen (kW)"],
    ["flex_discharge", "Batterij ontladen (kW)"],
    ["curtailed_production", "Uitgeschakelde productie (kW)"],
    ["uncurtailed_production", "Effectieve productie (kW)"],
    ["grid", "Net (kW)"],
    ["grid_offtake", "Net afname (kW)"],
    ["grid_inject", "Net injectie (kW)"],
    ["soc", "State of charge (%)"],
    ["imb_price", "Onbalans prijs (€/MWh)"],
    ["epex", "Epex prijs (€/MWh)"],
    ["forecast", "Forecast (kWh)"],
    ["nominated", "Nominated (kWh)"],
    ["actual", "Actual (kWh)"],
    ["credit_increment", "Flexibility compensation increment (€)"]
])

export default function Export() {

    const theme: CustomTheme = useTheme();
    const {t} = useTranslation();

    const {
        api,
        activeSmartBox
    } = useData();
    const {siteApi} = api || {};

    const [selectedFromDate, setSelectedFromDate] = useState<Date | null>(new Date());
    const [selectedToDate, setSelectedToDate] = useState<Date | null>(new Date());
    const [isDownloadingExport, setIsDownloadingExport] = useState<boolean>(false);
    const [selectedExportColumns, setSelectedExportColumns] = useState<GetDataExportApiSitesSiteIdMeteringExportGetColumnsEnum[]>(
        Object.values(GetDataExportApiSitesSiteIdMeteringExportGetColumnsEnum).filter(column => column !== "utctime"));

    const exportData = () => {
        let start: Date = startOfDay(selectedFromDate);
        let end: Date = startOfDay(new Date(selectedToDate).setDate(selectedToDate.getDate() + 1));
        if (activeSmartBox?.siteId) {
            setIsDownloadingExport(true);
            siteApi.getDataExportApiSitesSiteIdMeteringExportGet(activeSmartBox?.siteId,
                selectedExportColumns,
                Math.floor(start.valueOf() / 1000),
                Math.floor(end.valueOf() / 1000),
                {responseType: 'blob'}
            ).then((response: any) => {
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                const contentDisposition = response.headers['content-disposition'];
                const filename = contentDisposition?.split('filename=')[1] || `export-${moment().format('yyyy-MM-DD-HH-mm')}.xlsx`;
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }).catch((e) => {
                console.error(e);
            }).finally(() => {
                setIsDownloadingExport(false);
            })
        }
    }

    function isLargeExportRange(): boolean {
        const msInDay: number = 1000 * 60 * 60 * 24;
        const diffInMs: number = Math.abs(selectedToDate.getTime() - selectedFromDate.getTime());
        return diffInMs > 31 * msInDay;
    }

    return <>
        <Box sx={{height: '100%', display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'center'}}>
            <Box sx={{display: 'flex', gap: '10px'}}>
                <LocalizationProvider dateAdapter={AdapterDateFns}
                                      adapterLocale={nl}>
                    <DatePicker
                        label={t('common.fromDate')}
                        disabled={isDownloadingExport}
                        value={selectedFromDate}
                        onChange={setSelectedFromDate}
                        maxDate={new Date()}
                        renderInput={(params) => <TextField size="small" {...params} InputLabelProps={{
                            ...params.InputLabelProps,
                            style: {color: theme.palette.text.primary}
                        }}/>}
                    />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}
                                      adapterLocale={nl}>
                    <DatePicker
                        label={t('common.toDate')}
                        disabled={isDownloadingExport}
                        value={selectedToDate}
                        onChange={setSelectedToDate}
                        maxDate={new Date()} // disables future dates
                        renderInput={(params) => <TextField size="small" {...params} InputLabelProps={{
                            ...params.InputLabelProps,
                            style: {color: theme.palette.text.primary}
                        }}/>}
                    />
                </LocalizationProvider>
                {isLargeExportRange() && <Typography display="flex" alignItems="center" fontSize={12}>{t('dashboard.advanced.largeDataRange')}</Typography>}
            </Box>
            <FormControl size="small" fullWidth>
                <InputLabel id="export-columns-label"
                            style={{color: theme.palette.text.primary}}>{t('dashboard.advanced.selectColumns')}</InputLabel>
                <Select
                    labelId="export-columns-label"
                    disabled={isDownloadingExport}
                    multiple
                    value={selectedExportColumns}
                    onChange={(event) => {
                        const value = event.target.value as GetDataExportApiSitesSiteIdMeteringExportGetColumnsEnum[];
                        setSelectedExportColumns(value);
                    }}
                    input={<OutlinedInput label={t('dashboard.advanced.selectColumns')}/>}
                    renderValue={(selected) => (selected as string[]).map((value: string) => columnTranslations.get(value)).join(', ')}
                >
                    {Object.values(GetDataExportApiSitesSiteIdMeteringExportGetColumnsEnum).filter(column => column !== "utctime").map((column) => (
                        <MenuItem key={column} value={column}>
                            <Checkbox checked={selectedExportColumns.indexOf(column) > -1}/>
                            <ListItemText primary={columnTranslations.get(column)}/>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button variant="contained" size="small" sx={{height: '30px', boxShadow: 'none', textTransform: 'none'}} fullWidth disabled={isDownloadingExport}
                    onClick={exportData}>
                {isDownloadingExport && <CircularProgress size={15}/>}
                {!isDownloadingExport && <Typography color={theme.palette.text.secondary} fontWeight={600}>Download</Typography>}
            </Button>
        </Box>
    </>
}